'use client';

import type { LinkProps } from 'next/link';
import Link from 'next/link';

import { STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import {
  clearOverlayHistoryEntry,
  hasOverlayHistoryEntry,
} from '@/hooks/useOverlayHistory';

interface CustomLinkProps
  extends
    Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      keyof LinkProps | 'ref'
    >,
    LinkProps {}

export const CustomLink = ({
  replace,
  children,
  onClick,
  onMouseEnter,
  onPointerDown,
  href,
  prefetch = 'auto',
  ref,
  ...props
}: CustomLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
  const router = useCustomRouter();
  //* true는 Link가 이미 받아 두고, false는 Next와 같게 hover에서도 받지 않는다
  const shouldPrefetchOnIntent = prefetch !== true && prefetch !== false;

  return (
    <Link
      {...props}
      ref={ref}
      href={href}
      replace={replace}
      prefetch={prefetch}
      onMouseEnter={(e) => {
        if (shouldPrefetchOnIntent) {
          router.prefetch(getPathnameFromHref(href));
        }
        onMouseEnter?.(e);
      }}
      onPointerDown={(event) => {
        //* 터치엔 hover가 없어 위 onMouseEnter 프리페치가 안 돌았다 — 손가락이 떨어지기 전에 시작한다
        if (shouldPrefetchOnIntent && event.pointerType !== 'mouse') {
          router.prefetch(getPathnameFromHref(href));
        }
        onPointerDown?.(event);
      }}
      onClick={(e) => {
        //* 마커 엔트리를 남겨두면 뒤로가기 한 번이 같은 화면으로 돌아가 안 눌린 것처럼 보인다
        if (
          !replace &&
          typeof href === 'string' &&
          isPlainLeftClick(e) &&
          !props.target &&
          hasOverlayHistoryEntry()
        ) {
          e.preventDefault();
          //* 마커를 먼저 걷어야 한다 — onClick이 오버레이를 닫으면 그 back이 아래 replace를 취소한다
          clearOverlayHistoryEntry();
          router.replace(href);
          onClick?.(e);

          return;
        }

        if (replace) {
          sessionStorage.setItem(STORAGE_KEY.REPLACE_MARK, 'true');
          sessionStorage.setItem(STORAGE_KEY.REPLACE_MARK_KB, 'true');
        }

        onClick?.(e);
      }}
    >
      {children}
    </Link>
  );
};

const isPlainLeftClick = (event: React.MouseEvent<HTMLAnchorElement>) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

const getPathnameFromHref = (href: LinkProps['href']): string => {
  if (typeof href === 'string') {
    // 절대 URL인 경우 pathname 추출
    if (href.startsWith('http://') || href.startsWith('https://')) {
      try {
        return new URL(href).pathname;
      } catch {
        return href;
      }
    }

    // 상대 경로인 경우 query string 제거 후 반환
    return href.split('?')[0].split('#')[0];
  }

  // UrlObject인 경우 pathname 사용
  return href.pathname || '';
};
